import React, { useState } from 'react';
import { translate } from 'translations/i18n';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import Steven, { ELEMENT_TYPES } from 'javascripts/helpers/steven';
import { Button, Notice, Text } from '@companycam/slab-web';
import { useSamplePremiumPlan } from './hooks';
import TrialPremiumModal1x from 'images/TrialPremiumModal@1x.png';
import TrialPremiumModal2x from 'images/TrialPremiumModal@2x.png';
import TrialPremiumModal3x from 'images/TrialPremiumModal@3x.png';
import TrialTimelineGraphic1x from 'images/TrialTimelineGraphic@1x.png';
import TrialTimelineGraphic2x from 'images/TrialTimelineGraphic@2x.png';
import TrialTimelineGraphic3x from 'images/TrialTimelineGraphic@3x.png';
import Spinner from '../../../shared/DesignSystem/Spinner';

// #region styled-components
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--cc_size_spacing_l) var(--cc_size_spacing_m);
  justify-content: space-between;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const TimelineImageContainer = styled.div`
  padding-right: var(--cc_size_spacing_m);
  width: 42px;
`;

const CancelSection = styled.div`
  padding: var(--cc_size_spacing_l);
  background-color: var(--cc_color_background_2);
  border-radius: var(--cc_size_border_radius_l);
  margin-top: -12px;
`;

const Image = styled.img`
  position: absolute;
  right: 0;
  display: block;
  height: auto;
  width: 40%;
  margin-top: -48px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 425px;
  margin: auto;
`;

const LoadingMessage = styled.p`
  font-weight: 600;
  text-align: center;
  margin-top: var(--cc_size_spacing_m);
  font-family: Averta, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const ConfirmButton = styled(Button)`
  background: var(--cc_color_button_background_primary);
  color: var(--cc_color_button_text_primary);
  margin-left: var(--cc_size_spacing_s);

  &:hover {
    background: var(--cc_color_button_background_primary_darken_15);
  }
`;

// #endregion

const LoadingSpinner = () => {
  return (
    <LoadingWrapper>
      <Spinner />
      <LoadingMessage>
        {translate('billing.v2.premiumTrial.trialLoading')}
      </LoadingMessage>
    </LoadingWrapper>
  );
};

const Content = ({ error }) => (
  <div>
    <Image
      src={TrialPremiumModal3x}
      srcSet={`${TrialPremiumModal1x}, ${TrialPremiumModal2x} 2x, ${TrialPremiumModal3x} 3x`}
      alt="Premium Trial feature"
    />
    <Text size="xxl" as="h1" ccMargin="none none xl" style={{ width: 300 }}>
      {translate('billing.v2.premiumTrial.premiumTrialModalTitle')}
    </Text>
    <TimelineContainer>
      <TimelineImageContainer>
        <img
          src={TrialTimelineGraphic3x}
          srcSet={`${TrialTimelineGraphic1x}, ${TrialTimelineGraphic2x} 2x, ${TrialTimelineGraphic3x} 3x`}
          alt="Premium Trial Timeline"
        />
      </TimelineImageContainer>
      <div>
        <Text
          family="heading"
          weight="800"
          size="m"
          as="h2"
          ccMargin="none none xxs"
        >
          {translate('billing.v2.premiumTrial.premiumTrialTimelineHeadingOne')}
        </Text>
        <Text size="xs" ccMargin="none none xl">
          {translate(
            'billing.v2.premiumTrial.premiumTrialTimelineSubHeadingOne',
          )}
        </Text>
        <Text
          family="heading"
          weight="800"
          size="m"
          as="h2"
          ccMargin="none none xxs"
        >
          {translate('billing.v2.premiumTrial.premiumTrialTimelineHeadingTwo')}
        </Text>
        <Text size="xs" ccMargin="none none xl">
          {translate(
            'billing.v2.premiumTrial.premiumTrialTimelineSubHeadingTwo',
          )}
        </Text>
        <Text
          family="heading"
          weight="800"
          size="m"
          as="h2"
          ccMargin="none none xxs"
        >
          {translate(
            'billing.v2.premiumTrial.premiumTrialTimelineHeadingThree',
          )}
        </Text>
        <Text size="xs" ccMargin="none none xl">
          {translate(
            'billing.v2.premiumTrial.premiumTrialTimelineSubHeadingThree',
          )}
        </Text>
      </div>
    </TimelineContainer>
    <CancelSection>
      <Text
        family="heading"
        weight="800"
        size="s"
        as="h2"
        ccMargin="none none xs"
        color="subtle"
      >
        {translate('billing.v2.premiumTrial.premiumTrialModalCancelHeading')}
      </Text>
      <Text size="xs" ccMargin="none" color="subtle">
        {translate('billing.v2.premiumTrial.premiumTrialModalCancelCopy')}
      </Text>
    </CancelSection>
    {error ? (
      <Notice ccMargin="l none none" color="destroy">
        {translate('billing.v2.premiumTrial.trialStartError')}
      </Notice>
    ) : null}
  </div>
);

const ModalContent = ({ loading, formatMessage, error }) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  return <Content formatMessage={formatMessage} error={error} />;
};

const PremiumTrialModal = ({ onRequestClose }) => {
  const [error, setError] = useState(null); // Define the error state
  const { samplePremiumPlan, loading } = useSamplePremiumPlan({
    onSuccess: onRequestClose,
    onError: (errMsg) => setError(errMsg),
  });

  return (
    <CCModal
      isOpen
      shouldCloseOnOverlayClick={!loading}
      showCloseButton={false}
      onRequestClose={onRequestClose}
      className="modal-content-wrap is-padless"
      hideFooter={loading}
      primaryAction={{
        component: (
          <ConfirmButton
            data-testid="modal__trial-premium-start"
            onClick={() => {
              Steven.elementClicked({
                id: 'premium_trial_modal_button',
                content: 'Start 14 Day Free Trial',
                type: ELEMENT_TYPES.BUTTON,
                action: 'premium_trial_started',
              });
              samplePremiumPlan();
            }}
          >
            {translate('billing.v2.premiumTrial.startTrial')}
          </ConfirmButton>
        ),
      }}
      secondaryAction={{
        label: translate('billing.v2.premiumTrial.nevermind'),
        onClick: onRequestClose,
        'data-testid': 'modal__trial-premium-cancel',
      }}
    >
      <ContentContainer>
        <ModalContent loading={loading} error={error} />
      </ContentContainer>
    </CCModal>
  );
};

Content.propTypes = {
  error: PropTypes.any,
};

ModalContent.propTypes = {
  loading: PropTypes.bool.isRequired,
  formatMessage: PropTypes.func.isRequired,
  error: PropTypes.any, // adjust the PropTypes as needed for the actual error type
};

PremiumTrialModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default PremiumTrialModal;
